import React, { Component } from 'react'
import {
  entries,
  flatMap,
  isEmpty,
  isEqual,
  keyBy,
  keys,
  mapValues,
  sortBy,
  sum,
  sumBy,
  transform,
  union,
  uniqBy,
  values
} from 'lodash'
import moment from 'moment'
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Label,
  Legend,
  Line,
  Tooltip,
  XAxis,
  YAxis,
  ResponsiveContainer
} from 'recharts'
import PropTypes from 'prop-types'

import {
  fetchAverageSpacing,
  fetchOccupationDelay,
  fetchKpiProductAmounts,
  fetchKpiWorkingHours,
  fetchKpiShipWeightLossDiff,
  fetchKpiProductWeightLossDiff
} from '../../../js/rest'
import KpiBarchartTooltip from '../kpiBarchartTooltip/KpiBarchartTooltip'
import SpinnerOverlay from '../../spinnerOverlay/SpinnerOverlay'
import { handleFetchError } from '../../../js/sentry'
import { chartMargins, legendStyling } from '../../../utils/renderUtils'
import { APP_TERMINAL_IDS, VISIT_DELAY_COLORS } from '../../../js/constants'
import { percentageTickFormatter } from '../../../utils/kpiUtils'
import FilterableBarChart from '../kpiCharts/filterableBarChart/FilterableBarChart'
import EmptyPlaceholderChart from '../kpiCharts/emptyPlaceholderChart/EmptyPlaceholderChart'

/**
 * Invoked while reducing average delays data
 * Converts each delay duration from milliseconds to hours
 * @param acc
 * @param delayValue
 * @param delayKey
 */
const delayTransformIteratee = (acc, delayValue, delayKey) =>
  Object.assign(acc, {
    [delayKey]: Math.ceil(moment.duration(delayValue).asHours())
  })

export default class KpiTrends extends Component {
  constructor(props) {
    super(props)
    this.state = {
      fetchingData: false
    }
  }

  static propTypes = {
    authToken: PropTypes.string,
    customFetch: PropTypes.func.isRequired,
    terminalId: PropTypes.string.isRequired,
    selection: PropTypes.object.isRequired,
    productColors: PropTypes.object.isRequired
  }

  async getShipWeightLossDiff() {
    // only relevant for yara
    if (this.props.terminalId !== APP_TERMINAL_IDS.cargillIzegem) {
      return null
    }
    try {
      const shipWeightLossDiff = await fetchKpiShipWeightLossDiff(
        this.props.terminalId,
        this.props.selection.startDate,
        this.props.selection.stopDate,
        this.props.authToken,
        this.props.customFetch
      )

      const data = shipWeightLossDiff.weightDifferencePerShipInPercentage.map(({ name, weightLossPercentage }) => ({
        name,
        loss: Math.round(weightLossPercentage),
        average: shipWeightLossDiff.averagePercentage
      }))

      const vesselList = union(shipWeightLossDiff.weightDifferencePerShipInPercentage.map(v => v.name))
      return { data, vesselList }
    } catch (err) {
      handleFetchError(err)
      return null
    }
  }

  async getWeightLossPerProduct() {
    if (this.props.terminalId !== APP_TERMINAL_IDS.cargillIzegem) {
      return null
    }

    try {
      const productWeightLossDiff = await fetchKpiProductWeightLossDiff(
        this.props.terminalId,
        this.props.selection.startDate,
        this.props.selection.stopDate,
        this.props.authToken,
        this.props.customFetch
      )

      const data = productWeightLossDiff.weightDifferencePerShipInPercentage.map(
        ({ product, weightLossPercentage }) => ({
          name: product,
          loss: Math.round(weightLossPercentage),
          average: productWeightLossDiff.averagePercentage
        })
      )

      return { data }
    } catch (err) {
      handleFetchError(err)
      return null
    }
  }

  async getKpiWorkingHours() {
    try {
      const workingHoursResponse = await fetchKpiWorkingHours(
        this.props.terminalId,
        this.props.selection.startDate,
        this.props.selection.stopDate,
        this.props.authToken,
        this.props.customFetch
      )

      return entries(workingHoursResponse).map(([date, workingHours]) => ({
        ...mapValues(keyBy(workingHours, 'type'), type => Math.ceil(moment.duration(type.duration).asHours())),
        date: moment(Number(date)).format('MMM YYYY'),
        total: sumBy(workingHours, type => Math.ceil(moment.duration(type.duration).asHours()))
      }))
    } catch (err) {
      handleFetchError(err)
      return null
    }
  }

  async getKpiProductAmounts() {
    try {
      const productAmounts = await fetchKpiProductAmounts(
        this.props.terminalId,
        this.props.selection.startDate,
        this.props.selection.stopDate,
        this.props.authToken,
        this.props.customFetch
      )

      const formattedProductAmounts = sortBy(productAmounts, 'date').map(({ date, products }) => ({
        ...mapValues(keyBy(products, 'name'), product => product.amount),
        date: moment(Number(date)).format('MMM YYYY'),
        total: sumBy(products, product => product.amount)
      }))

      const uniqueProducts = uniqBy(
        flatMap(productAmounts, n => n.products),
        'name'
      )
      return {
        data: formattedProductAmounts,
        // productList is a string array with unique product names. used to dynamically render all products in result
        productList: sortBy(uniqueProducts, 'name').map(p => p.name)
      }
    } catch (err) {
      handleFetchError(err)
      return null
    }
  }

  async getKpiAverageSpacing() {
    try {
      const avgSpacing = await fetchAverageSpacing(
        this.props.terminalId,
        this.props.selection.startDate,
        this.props.selection.stopDate,
        this.props.authToken,
        this.props.customFetch
      )

      const totalVisits = sumBy(avgSpacing, 'numberOfVisits')
      return avgSpacing.map(spacing => ({
        ...spacing,
        totalVisitPercentage: Math.ceil(100 * (spacing.numberOfVisits / totalVisits))
      }))
    } catch (err) {
      handleFetchError(err)
      return null
    }
  }

  async getKpiDelays() {
    try {
      const delayData = await fetchOccupationDelay(
        this.props.terminalId,
        this.props.selection.startDate,
        this.props.selection.stopDate,
        this.props.authToken,
        this.props.customFetch
      )

      const formattedDelayData = flatMap(delayData, d => ({
        date: moment(Number(d.date)).format('MMM YYYY'),
        ...transform(d.delays, delayTransformIteratee, {}),
        total: Math.ceil(moment.duration(sum(values(d.delays))).asHours())
      }))

      return {
        data: formattedDelayData,
        // delayList used for dynamically rendering bar chart
        delayList: union(...delayData.map(d => keys(d.delays)))
      }
    } catch (err) {
      handleFetchError(err)
      return null
    }
  }

  fetchAllData() {
    this.setState({
      fetchingData: true
    })
    Promise.all([
      this.getKpiWorkingHours(),
      this.getKpiProductAmounts(),
      this.getKpiAverageSpacing(),
      this.getKpiDelays(),
      this.getShipWeightLossDiff(),
      this.getWeightLossPerProduct()
    ]).then(([workingHours, products, averageSpacing, delays, shipWeightLossDiff, productWeightLossDiff]) =>
      this.setState({
        workingHoursData: workingHours,
        products,
        averageSpacing,
        delays,
        shipWeightLossDiff,
        productWeightLossDiff,
        fetchingData: false
      })
    )
  }

  componentDidUpdate(prevProps) {
    const hasSelectionChanged = !isEqual(this.props.selection, prevProps.selection)
    const hasSelectedTerminalChanged = this.props.terminalId !== prevProps.terminalId

    if (hasSelectionChanged || hasSelectedTerminalChanged) {
      this.fetchAllData()
    }
  }

  componentDidMount() {
    if (this.props.selection.startDate > 0 && this.props.selection.stopDate > 0) {
      this.fetchAllData()
    }
  }

  render() {
    const { workingHoursData, products, averageSpacing, delays, shipWeightLossDiff, productWeightLossDiff } = this.state
    // only render charts if all required data is present
    const canRenderWorkingHoursChart = !isEmpty(workingHoursData)
    const canRenderProductAmountChart = products && !isEmpty(products.productList)
    const canRenderAverageSpacingChart = !isEmpty(averageSpacing)
    const canRenderDelaysChart = delays && !isEmpty(delays.delayList)
    const canRenderShipWeightLossDiffChart = shipWeightLossDiff && !isEmpty(shipWeightLossDiff.vesselList)
    const canRenderProductWeightLossDiffChart = productWeightLossDiff ? true : false

    return (
      <div className="kpi-chart-container">
        <div className="chart-group">
          <div className="chart-item">
            {canRenderWorkingHoursChart ? (
              <FilterableBarChart
                chartMargins={chartMargins}
                data={this.state.workingHoursData}
                colors={{
                  REGULAR: '#023858',
                  EVENING: '#035A8D',
                  WEEKEND: '#0570B0'
                }}
                labels={['REGULAR', 'EVENING', 'WEEKEND']}
                dataKey={'date'}
                xAxisLabel={'Total working hours'}
                yAxisLabel={'Hours'}
                xAxisAngle={-45}
              />
            ) : (
              <EmptyPlaceholderChart xAxisLabel={'Total product amounts'} yAxisLabel={'Amount (mt)'} />
            )}
          </div>
          <div className="chart-item">
            {canRenderProductAmountChart ? (
              <FilterableBarChart
                chartMargins={chartMargins}
                data={this.state.products.data}
                colors={this.props.productColors}
                labels={this.state.products.productList}
                dataKey={'date'}
                xAxisLabel={'Total product amounts'}
                yAxisLabel={'Amount (mt)'}
                xAxisAngle={-45}
              />
            ) : (
              <EmptyPlaceholderChart xAxisLabel={'Total product amounts'} yAxisLabel={'Amount (mt)'} />
            )}
          </div>

          <div className="chart-item">
            {canRenderAverageSpacingChart ? (
              <ResponsiveContainer width="100%" aspect={4.0 / 3.0}>
                <ComposedChart data={averageSpacing} margin={chartMargins}>
                  <XAxis dataKey="hours" interval={0}>
                    <Label value="Average discharge spacing (hours)" offset={-3} position="insideBottom" />
                  </XAxis>
                  <YAxis yAxisId="left" orientation="left">
                    <Label
                      value="Number of visits"
                      position="insideLeft"
                      angle={-90}
                      style={{ textAnchor: 'middle' }}
                    />
                  </YAxis>
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    type="number"
                    domain={[0, 100]}
                    tickFormatter={percentageTickFormatter}
                  >
                    <Label
                      value="Percentage of total visits"
                      position="insideRight"
                      angle={-90}
                      style={{ textAnchor: 'middle' }}
                    />
                  </YAxis>
                  <Tooltip content={<KpiBarchartTooltip labelSuffix="hours" />} />
                  <Legend verticalAlign="top" wrapperStyle={legendStyling} />
                  <CartesianGrid stroke="#f5f5f5" />
                  <Bar yAxisId="left" dataKey="numberOfVisits" name="Number of visits" fill="#1E70B0" />
                  <Line
                    type="monotone"
                    yAxisId="right"
                    dataKey="totalVisitPercentage"
                    name="Percentage of total visits"
                    unit="%"
                    stroke="#87A878"
                    strokeWidth={2}
                    activeDot={{ r: 2 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <EmptyPlaceholderChart xAxisLabel={'Average discharge spacing (hours)'} yAxisLabel={'Number of visits'} />
            )}
          </div>

          <div className="chart-item">
            {canRenderDelaysChart ? (
              <FilterableBarChart
                chartMargins={chartMargins}
                data={delays.data}
                colors={VISIT_DELAY_COLORS}
                labels={delays.delayList}
                dataKey={'date'}
                xAxisLabel={'Total delays'}
                yAxisLabel={'Hours'}
                xAxisAngle={-45}
              />
            ) : (
              <EmptyPlaceholderChart xAxisLabel={'Total delays'} yAxisLabel={'Hours'} width={800} height={600} />
            )}
          </div>

          {/*for now only relevant for Cargill Izegem*/}
          {this.props.terminalId === APP_TERMINAL_IDS.cargillIzegem && (
            <div className="chart-item">
              {canRenderShipWeightLossDiffChart ? (
                <ResponsiveContainer width="100%" aspect={4.0 / 3.0}>
                  <ComposedChart data={shipWeightLossDiff.data} margin={chartMargins} layout="vertical">
                    <CartesianGrid stroke="#f5f5f5" />
                    <YAxis type="category" dataKey="name" width={135} />
                    <XAxis type="number" label={{ value: 'Weight loss difference', position: 'bottom', offset: -5 }} />
                    <Tooltip content={<KpiBarchartTooltip />} />
                    <Bar dataKey="loss" name="Weight loss difference per ship" unit="mt" maxBarSize={80} fill="#66A61E">
                      {shipWeightLossDiff.vesselList.map((vessel, idx) => (
                        <Cell key={`cell-${idx}`} />
                      ))}
                    </Bar>
                    <Line dataKey="average" stroke="#82ca9d" name="Average difference" unit="mt" />
                    <Legend verticalAlign="top" wrapperStyle={legendStyling} />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <EmptyPlaceholderChart xAxisLabel={'Weight loss difference'} yAxisLabel={''} width={800} height={600} />
              )}
            </div>
          )}

          {/*for now only relevant for Cargill Izegem*/}
          {this.props.terminalId === APP_TERMINAL_IDS.cargillIzegem && (
            <div className="chart-item">
              {canRenderProductWeightLossDiffChart ? (
                <ResponsiveContainer width="100%" aspect={4.0 / 3.0}>
                  <ComposedChart data={productWeightLossDiff.data} margin={chartMargins} layout="vertical">
                    <CartesianGrid stroke="#f5f5f5" />
                    <YAxis type="category" dataKey="name" width={135} />
                    <XAxis type="number" label={{ value: 'Weight loss difference', position: 'bottom', offset: -5 }} />
                    <Tooltip content={<KpiBarchartTooltip />} />
                    <Bar
                      dataKey="loss"
                      name="Weight loss difference per product"
                      unit="mt"
                      maxBarSize={80}
                      fill="#66A61E"
                    ></Bar>
                    <Line dataKey="average" stroke="#82ca9d" name="Average difference" unit="mt" />
                    <Legend verticalAlign="top" wrapperStyle={legendStyling} />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <EmptyPlaceholderChart xAxisLabel={'Weight loss difference'} yAxisLabel={''} width={800} height={600} />
              )}
            </div>
          )}
        </div>

        <SpinnerOverlay loading={this.state.fetchingData} />
      </div>
    )
  }
}
